import { onlineDatePresets, FLEET_STATUS } from './fleetStatusUtils';

// The filter model and its URL encoding, kept together: a filter that gains a
// key must gain a param, and having both in one file makes that hard to miss.

// Filters that accept several values at once. Each holds an array; an empty
// array means "no restriction". Selecting two values is OR — "Down or
// Inaccessible", never both at once.
export const MULTI_FILTERS = [
  'status',
  'region',
  'market',
  'accountTier',
  'stockingStatus',
  'jofemarState',
  'peripheralDown',
  'reason',
];

// The subset whose values compare straight against a row field of the same
// name. `peripheralDown` (values are field names) and `reason` (the row holds a
// list) need their own clauses in the container.
export const DIRECT_MULTI_FILTERS = [
  'status',
  'region',
  'market',
  'accountTier',
  'stockingStatus',
  'jofemarState',
];

// Yes/no and free-text filters stay scalar — "Out of order: Yes and No" is just
// "no filter", and a second search term would need AND/OR semantics.
const SCALAR_FILTERS = [
  'outOfOrder',
  'safetyHold',
  'workOrders',
  'search',
  'matchMode',
];
// Everything that takes yes / no / any. Validated and URL-encoded together.
const YES_NO_FILTERS = ['outOfOrder', 'safetyHold', 'workOrders'];

// The subset the container's generic `passesBool` loop can handle, because the
// row field is a plain boolean. `workOrders` is deliberately absent: its field
// is tri-state (null = not loaded, [] = none open, [...] = open), and coercing
// that with `!!` would report "no open work orders" for a fridge we know
// nothing about. It gets its own clause.
export const BOOL_FILTERS = ['outOfOrder', 'safetyHold'];

/**
 * Split the location box into terms. Any run of whitespace, commas or
 * semicolons separates, so a list typed or pasted with spaces behaves the same
 * as one with commas. Terms are OR-ed, and each matches as a substring, so
 * partial IDs like "ICNC" still pull in every ICNC_* fridge.
 *
 * @param {string} value
 * @returns {string[]} Trimmed, de-duplicated, empties dropped.
 */
export function searchTerms(value) {
  if (typeof value !== 'string') return [];
  return Array.from(new Set(value.split(/[\s,;]+/).filter(Boolean)));
}

// Search matches whole location IDs instead of substrings. Opt-in, because the
// two intents are indistinguishable in the text: "ICNC" is both a complete ID
// and the prefix of four others, so only the reader knows which they meant.
export const MATCH_EXACT = 'exact';

/**
 * Does a row satisfy the location search?
 *
 * Contains mode (default) tests each term against "<id> <name>", so a keyword
 * pulls in every fridge whose ID or name carries it. Exact mode compares whole
 * location IDs and ignores the name — a pasted list names specific fridges, and
 * matching their display text would re-introduce the prefix problem it exists
 * to solve. Terms are OR-ed either way.
 *
 * @param {string} locationId
 * @param {string} prettyName
 * @param {string} value Raw search box text.
 * @param {string} mode MATCH_EXACT, or anything else for contains.
 * @returns {boolean} True when the search is empty — no search, no restriction.
 */
export function matchesSearch(locationId, prettyName, value, mode) {
  const terms = searchTerms(value);
  if (terms.length === 0) return true;
  if (mode === MATCH_EXACT) {
    const id = String(locationId).toLowerCase();
    return terms.some((term) => term.toLowerCase() === id);
  }
  const haystack = `${locationId} ${prettyName}`.toLowerCase();
  return terms.some((term) => haystack.includes(term.toLowerCase()));
}

export function emptyFilters() {
  const filters = { onlineFrom: '', onlineTo: '' };
  SCALAR_FILTERS.forEach((key) => {
    filters[key] = '';
  });
  MULTI_FILTERS.forEach((key) => {
    filters[key] = [];
  });
  return filters;
}

// Nothing is filtered by default. The page used to open with "IP set: yes",
// which existed to hide fridges the evaluator could only ever call
// INACCESSIBLE; those now carry their own NO_IP status and sort to the bottom,
// so hiding them would remove a row set nobody asked to lose.

// Sort travels in the URL too, so a shared link reproduces the view and not
// just the row set. Kept out of `filters` — it changes ordering, not membership,
// and the chip row must not offer to "clear" it.
const SORT_PARAM = 'sort';
const DIR_PARAM = 'dir';
export const DEFAULT_SORT = { key: 'problems', dir: 'asc' };

/**
 * @param {Object} sort {key, dir}
 * @param {string[]} sortKeys Keys the table can actually sort by, so a stale
 *   link cannot select an ordering the page no longer has.
 */
export function sortToParams(sort, params) {
  if (!sort || sort.key === DEFAULT_SORT.key) {
    if (sort && sort.dir !== DEFAULT_SORT.dir) params.set(DIR_PARAM, sort.dir);
    return;
  }
  params.set(SORT_PARAM, sort.key);
  if (sort.dir !== DEFAULT_SORT.dir) params.set(DIR_PARAM, sort.dir);
}

export function searchToSort(search, sortKeys) {
  const params = new URLSearchParams(search);
  const key = params.get(SORT_PARAM);
  const dir = params.get(DIR_PARAM) === 'desc' ? 'desc' : DEFAULT_SORT.dir;
  const valid = key === DEFAULT_SORT.key || sortKeys.includes(key);
  return { key: valid ? key : DEFAULT_SORT.key, dir };
}

const ONLINE_PARAM = 'online';

// Links shared before "IP set" became a status. `no` named exactly the rows
// NO_IP names now; `yes` and the cleared token described the whole rest of the
// fleet, which is what no filter already means.
const LEGACY_IP_PARAM = 'ipConfigured';

/** The preset whose range equals this one, if any. */
function matchingPreset(filters, today) {
  const presets = onlineDatePresets(today);
  return Object.keys(presets).find(
    (key) =>
      presets[key].from === filters.onlineFrom &&
      presets[key].to === filters.onlineTo,
  );
}

/**
 * Filters as a query string. Arrays become repeated keys (?status=UP&status=
 * DOWN) rather than a joined value: reasons are free-form and may contain
 * commas, and repeated keys need no escaping rules.
 *
 * A range that matches a preset is written as the preset NAME, so a shared link
 * still means "online now" tomorrow instead of pinning today's date.
 *
 * @returns {string} No leading "?"; empty when the filters are the defaults.
 */
export function filtersToSearch(filters, today, sort) {
  const params = new URLSearchParams();
  MULTI_FILTERS.forEach((key) => {
    filters[key].forEach((value) => params.append(key, value));
  });
  SCALAR_FILTERS.forEach((key) => {
    if (filters[key]) params.set(key, filters[key]);
  });

  const preset = matchingPreset(filters, today);
  if (preset) {
    params.set(ONLINE_PARAM, preset);
  } else if (filters.onlineFrom || filters.onlineTo) {
    if (filters.onlineFrom) params.set('onlineFrom', filters.onlineFrom);
    if (filters.onlineTo) params.set('onlineTo', filters.onlineTo);
  }
  // An empty range is the default now, so absence says it.
  sortToParams(sort, params);
  return params.toString();
}

/**
 * Rebuild filters from a query string. An absent param means "unset" — nothing
 * is filtered by default.
 *
 * Unknown values are kept as-is rather than dropped: a region that no longer
 * exists simply matches nothing, which is visible, whereas silently ignoring it
 * would show MORE rows than the link's author saw. The exception is the yes/no
 * filters, where an unrecognised value would quietly read as "no".
 */
export function searchToFilters(search, today) {
  const params = new URLSearchParams(search);
  const filters = emptyFilters();

  MULTI_FILTERS.forEach((key) => {
    filters[key] = params.getAll(key);
  });
  SCALAR_FILTERS.forEach((key) => {
    filters[key] = params.get(key) || '';
  });
  YES_NO_FILTERS.forEach((key) => {
    if (filters[key] !== 'yes' && filters[key] !== 'no') filters[key] = '';
  });
  // Anything but the one known mode means contains, so a stale link cannot
  // narrow the row set in a way the label does not describe.
  if (filters.matchMode !== MATCH_EXACT) filters.matchMode = '';
  if (
    params.get(LEGACY_IP_PARAM) === 'no' &&
    !filters.status.includes(FLEET_STATUS.NO_IP)
  ) {
    filters.status = [...filters.status, FLEET_STATUS.NO_IP];
  }

  const online = params.get(ONLINE_PARAM);
  const presets = onlineDatePresets(today);
  if (online && presets[online]) {
    return {
      ...filters,
      onlineFrom: presets[online].from,
      onlineTo: presets[online].to,
    };
  }
  if (params.has('onlineFrom') || params.has('onlineTo')) {
    return {
      ...filters,
      onlineFrom: params.get('onlineFrom') || '',
      onlineTo: params.get('onlineTo') || '',
    };
  }
  // Absent, or a legacy `online=any` from a link shared before the online
  // default was dropped: either way, no online filter.
  return filters;
}
